import { useEffect } from 'react';
import { useStressTestStore } from '../../store/stressTestStore';
import { useScenarioStore } from '../../store/scenarioStore';
import type { PlanningRolloutStep } from '../../types/planningRollout';
import { fmt } from '../../utils/numberFormat';

const pct = (v: number) => `${Math.round(v * 100)}`;

const SHARE_KEYS: { key: keyof Omit<PlanningRolloutStep, 'year'>; label: string; title: string }[] = [
  { key: 'pvShare', label: 'PV', title: 'Anteil Hausanschlüsse mit PV-Anlage' },
  { key: 'heatPumpShare', label: 'WP', title: 'Anteil Hausanschlüsse mit Wärmepumpe' },
  { key: 'wallboxShare', label: 'WB', title: 'Anteil Hausanschlüsse mit Wallbox' },
  { key: 'storageShare', label: 'Sp.', title: 'Anteil Hausanschlüsse mit Batteriespeicher' },
  { key: 'acShare', label: 'Kl.', title: 'Anteil Hausanschlüsse mit Klimaanlage (wirkt im Sommerabend-Lastfall)' },
];

/**
 * Hochlaufpfad editor: target shares of house connections per Stützjahr,
 * Monte-Carlo runs and seed. Defaults are assumptions (NEP-derived) and the
 * observed Bestand is shown as the starting point.
 */
export function RolloutCard({ hasResults }: { hasResults: boolean }) {
  const stressZoneId = useStressTestStore(s => s.stressZoneId);
  const defaults = useStressTestStore(s => s.rolloutDefaults);
  const loadRolloutDefaults = useStressTestStore(s => s.loadRolloutDefaults);
  const path = useStressTestStore(s => s.rolloutPath);
  const setRolloutPath = useStressTestStore(s => s.setRolloutPath);
  const runs = useStressTestStore(s => s.rolloutRuns);
  const setRolloutRuns = useStressTestStore(s => s.setRolloutRuns);
  const seed = useStressTestStore(s => s.rolloutSeed);
  const setRolloutSeed = useStressTestStore(s => s.setRolloutSeed);
  const imsysShare = useStressTestStore(s => s.rolloutImsysShare);
  const setRolloutImsysShare = useStressTestStore(s => s.setRolloutImsysShare);
  const baseLoadDrift = useStressTestStore(s => s.rolloutBaseLoadDrift);
  const setRolloutBaseLoadDrift = useStressTestStore(s => s.setRolloutBaseLoadDrift);
  const addedYears = useStressTestStore(s => s.rolloutAddedYears);
  const setRolloutAddedYear = useStressTestStore(s => s.setRolloutAddedYear);
  const addedElements = useScenarioStore(s => s.pendingAddedElements);
  const baseCase = useStressTestStore(s => s.baseCase);
  const setBaseCase = useStressTestStore(s => s.setBaseCase);
  const isControlActive = useStressTestStore(s => s.isControlActive);
  const setControlActive = useStressTestStore(s => s.setControlActive);
  const availability = useStressTestStore(s => s.availability);

  useEffect(() => {
    if (stressZoneId) {
      void loadRolloutDefaults(stressZoneId);
    }
  }, [stressZoneId, loadRolloutDefaults]);

  const update = (index: number, patch: Partial<PlanningRolloutStep>) => {
    setRolloutPath(path.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };
  const remove = (index: number) => setRolloutPath(path.filter((_, i) => i !== index));
  const add = () => {
    const last = path[path.length - 1];
    const next: PlanningRolloutStep = last
      ? { ...last, year: last.year + 5, pvShare: Math.min(1, last.pvShare + 0.1), heatPumpShare: Math.min(1, last.heatPumpShare + 0.1), wallboxShare: Math.min(1, last.wallboxShare + 0.1), storageShare: Math.min(1, last.storageShare + 0.1), acShare: Math.min(1, last.acShare + 0.1) }
      : { year: new Date().getFullYear() + 1, pvShare: 0.3, heatPumpShare: 0.15, wallboxShare: 0.2, storageShare: 0.15, acShare: 0.3 };
    setRolloutPath([...path, next]);
  };
  const resetToDefaults = () => {
    if (defaults) setRolloutPath(defaults.path);
  };
  const empirical = defaults?.empirical;
  const hasEmpiricalRate = !!empirical && (empirical.pvSharePerYear != null || empirical.heatPumpSharePerYear != null || empirical.wallboxSharePerYear != null || empirical.storageSharePerYear != null || empirical.acSharePerYear != null);
  const loadEmpirical = () => {
    if (empirical) setRolloutPath(empirical.extrapolatedPath);
  };
  const perYear = (v?: number) => (v == null ? '–' : `${(v * 100).toFixed(1)} %/a`);

  const bestand = defaults?.bestand;
  const houseCount = defaults?.houseCount ?? 0;
  const estimatedSolves = 3 * (1 + path.length * runs);

  // Targets below the observed Bestand mean the engine adds nothing for that
  // type until the path catches up — say so instead of failing silently.
  const belowBestand = bestand
    ? SHARE_KEYS.map(k => ({
        label: k.label,
        years: path.filter(step => (step[k.key] as number) < (bestand[k.key] as number) - 1e-9).map(step => step.year),
      })).filter(e => e.years.length > 0)
    : [];

  return (
    <div className={`rounded-lg border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-700 overflow-hidden ${hasResults ? 'opacity-90' : ''}`}>
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-800 dark:text-primary-100">Hochlaufpfad</h3>
          {defaults && (
            <span className="flex gap-2">
              <button type="button" onClick={resetToDefaults} title="NEP-Zuwachs ab dem Bestand dieser Zone" className="text-[10px] text-accent-primary hover:underline">NEP ab Bestand</button>
              {hasEmpiricalRate && (
                <button type="button" onClick={loadEmpirical} className="text-[10px] text-accent-primary hover:underline" title={empirical?.source}>Weiter wie bisher</button>
              )}
            </span>
          )}
        </div>

        {bestand && (
          <p className="text-[11px] text-gray-600 dark:text-primary-300 mb-2 leading-snug">
            Bestand heute ({houseCount} Hausanschlüsse): PV {pct(bestand.pvShare)} %, WP {pct(bestand.heatPumpShare)} %, Wallbox {pct(bestand.wallboxShare)} %, Speicher {pct(bestand.storageShare)} %, Klima {pct(bestand.acShare)} %.
          </p>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-[11px] tabular-nums">
            <thead>
              <tr className="text-gray-500 dark:text-primary-400">
                <th className="text-left font-medium pb-1">Jahr</th>
                {SHARE_KEYS.map(k => <th key={k.key} className="text-right font-medium pb-1" title={k.title}>{k.label} %</th>)}
                <th />
              </tr>
            </thead>
            <tbody>
              {path.map((step, i) => (
                <tr key={i} className="border-t border-gray-100 dark:border-primary-600">
                  <td className="py-0.5 pr-1">
                    <input
                      type="number"
                      value={step.year}
                      min={new Date().getFullYear()}
                      max={2100}
                      onChange={(e) => update(i, { year: Number(e.target.value) })}
                      className="w-14 px-1 py-0.5 border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100"
                    />
                  </td>
                  {SHARE_KEYS.map(k => (
                    <td key={k.key} className="py-0.5 text-right">
                      <input
                        type="number"
                        value={Math.round((step[k.key] as number) * 100)}
                        min={0}
                        max={100}
                        onChange={(e) => update(i, { [k.key]: Math.max(0, Math.min(100, Number(e.target.value))) / 100 } as Partial<PlanningRolloutStep>)}
                        className="w-11 px-1 py-0.5 text-right border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100"
                      />
                    </td>
                  ))}
                  <td className="py-0.5 pl-1 text-right">
                    <button type="button" onClick={() => remove(i)} title="Stützjahr entfernen" className="text-gray-400 hover:text-red-600 dark:hover:text-red-400">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" onClick={add} disabled={path.length >= 8} className="mt-1 text-[11px] text-accent-primary hover:underline disabled:opacity-50">+ Stützjahr</button>
        {belowBestand.length > 0 && (
          <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1 leading-snug">
            Ziel unter Bestand — kein weiterer Zubau: {belowBestand.map(e => `${e.label} (${e.years.join(', ')})`).join(' · ')}
          </p>
        )}
        <p className="text-[10px] text-gray-500 dark:text-primary-400 mt-1 leading-snug">
          {defaults?.pathSource ?? 'Annahmen, überschreibbar.'}
        </p>
        {defaults?.propensitySource && (
          <p className="text-[10px] text-gray-500 dark:text-primary-400 mt-1 leading-snug">
            Propensity: {defaults.propensitySource}
          </p>
        )}
        {empirical && (
          <p className="text-[10px] text-gray-500 dark:text-primary-400 mt-1 leading-snug" title={empirical.source}>
            Beobachtete Zubaurate ({empirical.windowYears} Jahre, {empirical.datedUnits} datierte Geräte{empirical.undatedUnits > 0 ? `, ${empirical.undatedUnits} ohne Datum` : ''}): PV {perYear(empirical.pvSharePerYear)} · WP {perYear(empirical.heatPumpSharePerYear)} · WB {perYear(empirical.wallboxSharePerYear)} · Sp. {perYear(empirical.storageSharePerYear)} · Kl. {perYear(empirical.acSharePerYear)}
            {!hasEmpiricalRate ? ' — zu wenig Installationsdaten für einen empirischen Pfad (MaStR-Import füllt sie).' : ''}
          </p>
        )}

        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="text-[11px] text-gray-600 dark:text-primary-300">
            Läufe je Stützjahr
            <input
              type="number"
              value={runs}
              min={1}
              max={defaults?.maxMonteCarloRuns ?? 50}
              onChange={(e) => setRolloutRuns(Number(e.target.value))}
              className="mt-0.5 w-full px-2 py-1 border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100"
            />
          </label>
          <label className="text-[11px] text-gray-600 dark:text-primary-300">
            Seed
            <input
              type="number"
              value={seed}
              min={0}
              onChange={(e) => setRolloutSeed(Number(e.target.value))}
              className="mt-0.5 w-full px-2 py-1 border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100"
            />
          </label>
        </div>
        <p className="text-[10px] text-gray-500 dark:text-primary-400 mt-1">
          ≈ {estimatedSolves} Lastflüsse (Starklast, Einspeisung, Sommerabend). Gleicher Seed = gleiche Zuordnung; ein Haus behält ein zugeteiltes Gerät über alle Stützjahre.
        </p>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="text-[11px] text-gray-600 dark:text-primary-300" title="Neuanlagen ohne iMSys + Steuerungseinrichtung speisen nach § 9 EEG (Solarspitzengesetz) höchstens 60 % ihrer Leistung ein; Anlagen mit iMSys speisen im Worst Case voll ein.">
            Neue PV mit iMSys (%)
            <input
              type="number"
              value={Math.round(imsysShare * 100)}
              min={0}
              max={100}
              onChange={(e) => setRolloutImsysShare(Math.max(0, Math.min(100, Number(e.target.value))) / 100)}
              className="mt-0.5 w-full px-2 py-1 border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100"
            />
          </label>
          <label className="text-[11px] text-gray-600 dark:text-primary-300" title="Skaliert die beobachteten Haus-Grundlasten je Stützjahr mit (1+d)^Jahre. Default 0: der klassische Haushaltsverbrauch stagniert (UBA/Destatis); WP/EV-Zuwachs ist bereits als Gerätezubau modelliert — Drift nur für Sondereffekte (z. B. Klimatisierung) setzen.">
            Grundlast-Drift (%/a)
            <input
              type="number"
              value={baseLoadDrift}
              min={-5}
              max={5}
              step={0.1}
              onChange={(e) => setRolloutBaseLoadDrift(Number(e.target.value))}
              className="mt-0.5 w-full px-2 py-1 border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100"
            />
          </label>
        </div>

        {addedElements.length > 0 && (
          <div className="mt-2">
            <p className="text-[11px] font-medium text-gray-700 dark:text-primary-200" title="Hinzugefügte Hausanschlüsse aus dem Szenario-Editor: ohne Jahr existieren sie ab heute, mit Jahr erst ab diesem Stützjahr (weder Last noch Gerätezubau davor).">Neubau — Häuser ab Stützjahr</p>
            {addedElements.map(el => (
              <div key={el.id} className="flex items-center justify-between gap-2 mt-0.5">
                <span className="text-[11px] text-gray-600 dark:text-primary-300 truncate" title={el.name || el.id}>{el.name || el.id}</span>
                <select
                  value={addedYears[el.id] ?? ''}
                  onChange={(e) => setRolloutAddedYear(el.id, e.target.value === '' ? null : Number(e.target.value))}
                  className="text-[11px] px-1 py-0.5 border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-800 text-gray-900 dark:text-primary-100"
                >
                  <option value="">ab heute</option>
                  {path.map(p => <option key={p.year} value={p.year}>ab {p.year}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 flex rounded-lg border border-gray-300 dark:border-primary-500 overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => setBaseCase({ mode: 'archive' })}
            className={`flex-1 px-2 py-1.5 font-medium ${baseCase.mode === 'archive' ? 'bg-accent-primary text-white' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200'}`}
          >
            Auf Archiv-Frames
          </button>
          <button
            type="button"
            onClick={() => setBaseCase({ mode: 'synthetic' })}
            className={`flex-1 px-2 py-1.5 font-medium ${baseCase.mode !== 'archive' ? 'bg-accent-primary text-white' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200'}`}
          >
            Synthetisch
          </button>
        </div>
        {baseCase.mode === 'archive' && availability && !availability.hasArchive && (
          <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">Kein Archiv — der Lauf fällt gekennzeichnet auf den synthetischen Worst Case zurück.</p>
        )}
        <label className="mt-2 flex items-center gap-2 text-xs text-gray-700 dark:text-primary-200 cursor-pointer">
          <input type="checkbox" checked={isControlActive} onChange={(e) => setControlActive(e.target.checked)} />
          <span>§14a-Steuerung aktiv <span className="text-[10px] text-gray-500 dark:text-primary-400">(P_min-Floor im Starklastfall)</span></span>
        </label>
        {houseCount > 0 && <p className="sr-only">{fmt(houseCount, 0)} Häuser</p>}
      </div>
    </div>
  );
}
